import { alpha } from '@mui/material/styles';
import { JLR_PLANT } from '../../constants';

export default function IconButton(theme) {
  const isJlr = theme.plantName === JLR_PLANT;
  const isDarkMode = theme.palette.mode === 'dark';

  const jlrIconButtonStyles = {
    borderRadius: '12px',
    padding: '8px',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    overflow: 'hidden',
    
    // Ripple effect enhancement
    '& .MuiTouchRipple-root': {
      color: isDarkMode 
        ? 'rgba(255, 255, 255, 0.3)'
        : 'rgba(45, 74, 45, 0.3)',
    },

    '&:hover': {
      background: isDarkMode 
        ? 'rgba(255, 255, 255, 0.08)'
        : 'rgba(45, 74, 45, 0.08)',
      transform: 'translateY(-1px)',
      boxShadow: isDarkMode 
        ? '0 4px 12px rgba(0, 0, 0, 0.2)'
        : '0 4px 12px rgba(45, 74, 45, 0.15)',
    },

    '&:active': {
      transform: 'translateY(0)',
    },

    '&.Mui-disabled': {
      opacity: 0.5,
      transform: 'none',
      boxShadow: 'none',
    },

    // Focus styles
    '&.Mui-focusVisible': {
      background: isDarkMode 
        ? 'rgba(255, 255, 255, 0.12)'
        : 'rgba(45, 74, 45, 0.12)',
      outline: `2px solid ${isDarkMode ? '#0066cc' : '#2d4a2d'}`,
      outlineOffset: '2px',
    },
  };

  const standardIconButtonStyles = {
    borderRadius: '50%',
    padding: '8px',
    
    '&:hover': {
      background: alpha(theme.palette.action.hover, 0.04),
    },
  };

  const baseStyles = isJlr ? jlrIconButtonStyles : standardIconButtonStyles;

  return {
    MuiIconButton: {
      styleOverrides: {
        root: baseStyles,

        // Size variants
        sizeSmall: {
          padding: isJlr ? '6px' : '4px',
          borderRadius: isJlr ? '8px' : '50%',
          
          '& .MuiSvgIcon-root': {
            fontSize: '1.25rem',
          },
        },

        sizeMedium: {
          padding: isJlr ? '8px' : '8px',
          borderRadius: isJlr ? '12px' : '50%',
          
          '& .MuiSvgIcon-root': {
            fontSize: '1.5rem',
          },
        },

        sizeLarge: {
          padding: isJlr ? '12px' : '12px',
          borderRadius: isJlr ? '16px' : '50%',
          
          '& .MuiSvgIcon-root': {
            fontSize: '1.75rem',
          },
        },

        // Color variants for JLR
        ...(isJlr && {
          colorPrimary: {
            color: isDarkMode ? '#0066cc' : '#2d4a2d',
            
            '&:hover': {
              background: isDarkMode 
                ? 'rgba(0, 102, 204, 0.1)'
                : 'rgba(45, 74, 45, 0.1)',
            },

            '&.Mui-focusVisible': {
              background: isDarkMode 
                ? 'rgba(0, 102, 204, 0.15)'
                : 'rgba(45, 74, 45, 0.15)',
            },
          },

          colorSecondary: {
            color: isDarkMode ? '#c8b99c' : '#9d8b6f',
            
            '&:hover': {
              background: isDarkMode 
                ? 'rgba(200, 185, 156, 0.1)'
                : 'rgba(200, 185, 156, 0.1)',
            },

            '&.Mui-focusVisible': {
              background: isDarkMode 
                ? 'rgba(200, 185, 156, 0.15)'
                : 'rgba(200, 185, 156, 0.15)',
            },
          },

          colorError: {
            color: '#dc3545',
            
            '&:hover': {
              background: isDarkMode 
                ? 'rgba(220, 53, 69, 0.1)'
                : 'rgba(220, 53, 69, 0.1)',
            },

            '&.Mui-focusVisible': {
              background: isDarkMode 
                ? 'rgba(220, 53, 69, 0.15)'
                : 'rgba(220, 53, 69, 0.15)',
            },
          },

          colorWarning: {
            color: '#ffa726',
            
            '&:hover': {
              background: isDarkMode 
                ? 'rgba(255, 167, 38, 0.1)'
                : 'rgba(255, 167, 38, 0.1)',
            },

            '&.Mui-focusVisible': {
              background: isDarkMode 
                ? 'rgba(255, 167, 38, 0.15)'
                : 'rgba(255, 167, 38, 0.15)',
            },
          },

          colorInfo: {
            color: '#1976d2',
            
            '&:hover': {
              background: isDarkMode 
                ? 'rgba(25, 118, 210, 0.1)'
                : 'rgba(25, 118, 210, 0.1)',
            },

            '&.Mui-focusVisible': {
              background: isDarkMode 
                ? 'rgba(25, 118, 210, 0.15)'
                : 'rgba(25, 118, 210, 0.15)',
            },
          },

          colorSuccess: {
            color: '#27ae60',
            
            '&:hover': {
              background: isDarkMode 
                ? 'rgba(39, 174, 96, 0.1)'
                : 'rgba(39, 174, 96, 0.1)',
            },

            '&.Mui-focusVisible': {
              background: isDarkMode 
                ? 'rgba(39, 174, 96, 0.15)'
                : 'rgba(39, 174, 96, 0.15)',
            },
          },
        }),

        // Edge variants (special positioning styles for JLR)
        ...(isJlr && {
          edgeStart: {
            marginLeft: '-6px',
          },
          edgeEnd: {
            marginRight: '-6px',
          },
        }),
      },
    },

    // Enhanced Fab styling for JLR
    MuiFab: {
      styleOverrides: {
        root: {
          ...(isJlr && {
            borderRadius: '20px',
            boxShadow: isDarkMode 
              ? '0 8px 32px rgba(0, 0, 0, 0.3)'
              : '0 8px 32px rgba(45, 74, 45, 0.15)',
            fontFamily: "'Inter', sans-serif",
            fontWeight: 600,
            textTransform: 'none',
            letterSpacing: '0.5px',
            
            '&:hover': {
              transform: 'translateY(-2px) scale(1.02)',
              boxShadow: isDarkMode 
                ? '0 12px 40px rgba(0, 0, 0, 0.4)'
                : '0 12px 40px rgba(45, 74, 45, 0.2)',
            },

            '&:active': {
              transform: 'translateY(0) scale(1)',
            },
          }),
        },

        primary: {
          ...(isJlr && {
            background: isDarkMode 
              ? 'linear-gradient(135deg, #0066cc, #004499)'
              : 'linear-gradient(135deg, #2d4a2d, #1a2d1a)',
            color: '#ffffff',
            
            '&:hover': {
              background: isDarkMode 
                ? 'linear-gradient(135deg, #004499, #002266)'
                : 'linear-gradient(135deg, #1a2d1a, #0f1a0f)',
            },
          }),
        },

        secondary: {
          ...(isJlr && {
            background: isDarkMode 
              ? 'linear-gradient(135deg, rgba(200, 185, 156, 0.9), rgba(157, 139, 111, 0.8))'
              : 'linear-gradient(135deg, #c8b99c, #9d8b6f)',
            color: isDarkMode ? '#ffffff' : '#2d4a2d',
            
            '&:hover': {
              background: isDarkMode 
                ? 'linear-gradient(135deg, rgba(200, 185, 156, 1), rgba(157, 139, 111, 0.9))'
                : 'linear-gradient(135deg, #9d8b6f, #6b5e4c)',
            },
          }),
        },

        sizeSmall: {
          width: isJlr ? '48px' : '40px',
          height: isJlr ? '48px' : '40px',
          
          '& .MuiSvgIcon-root': {
            fontSize: '1.25rem',
          },
        },

        sizeMedium: {
          width: isJlr ? '56px' : '56px',
          height: isJlr ? '56px' : '56px',
          
          '& .MuiSvgIcon-root': {
            fontSize: '1.5rem',
          },
        },

        sizeLarge: {
          width: isJlr ? '64px' : '64px',
          height: isJlr ? '64px' : '64px',
          
          '& .MuiSvgIcon-root': {
            fontSize: '1.75rem',
          },
        },

        extended: {
          ...(isJlr && {
            borderRadius: '24px',
            padding: '12px 24px',
            fontSize: '0.875rem',
            
            '& .MuiSvgIcon-root': {
              marginRight: '8px',
              fontSize: '1.25rem',
            },
          }),
        },
      },
    },
  };
}
